import { useEffect, useState } from "react";
import { deleteCompany, getAllCompanies } from "../../../services/companiesService";
import { useAuth } from "../../../context/authContext";
import { Trash2, ChevronDown, Filter, SortAsc, SortDesc, Search, X } from "lucide-react";
import { ActionLoader, ErrorPage, PageLoader } from "../../loader";
import Swal from "sweetalert2";
import { Pagination } from "@mui/material";
import { Banner } from "../../common/Banner";

const MemberList = ({ onSelectCompany, companies, reload }) => {
  const { user } = useAuth();

  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para filtros y ordenamiento
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("trade_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterPeriodicityOpen, setFilterPeriodicityOpen] = useState(false);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState("");
  const itemsPerPage = 15;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPeriodicity, sortBy, sortDirection]);


  const handleDelete = async (company) => {
    const { isConfirmed } = await Swal.fire({
      title: `¿Eliminar a ${company.trade_name}?`,
      text: "Esta acción no se puede deshacer, y eliminará todos los datos asociados a este socio, noticias, contactos, cuotas sociales y pagos.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280"
    });
    if (!isConfirmed) return;

    try {
      setIsSubmitting(true);
      const response = await deleteCompany(company.rut, user);
      setIsSubmitting(false);
      if (response.success){
        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: `${company.trade_name} ha sido eliminado correctamente`,
          timer: 1500
        });
        reload();
      }
      else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo eliminar el socio"
        });
      }
    } catch (error) {
      console.error("Error al eliminar el socio:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo eliminar el socio"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return new Date(0);
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedPeriodicity("");
    setSortBy("trade_name");
    setSortDirection("asc");
  };

  // Aplicar filtros y ordenamiento
  const filteredAndSorted = companies
  .filter(c => {
    const search = searchTerm.toLowerCase();
    const tradeName = c.trade_name?.toLowerCase() || "";
    const rut = c.rut?.toLowerCase() || "";
    const email = c.contact_email?.toLowerCase() || "";

    return (
      (tradeName.includes(search) ||
       rut.includes(search) ||
       email.includes(search)) &&
      (selectedPeriodicity === "" || c.periodicity === selectedPeriodicity)
    );
  })
  .sort((a, b) => {
    if (sortBy === "trade_name") {
      return sortDirection === "asc"
        ? a.trade_name.localeCompare(b.trade_name)
        : b.trade_name.localeCompare(a.trade_name);
    } else if (sortBy === "entry_date") {
      const dateA = parseDateString(a.entry_date);
      const dateB = parseDateString(b.entry_date);
      return sortDirection === "asc" 
        ? dateA - dateB 
        : dateB - dateA;
    }
    return a.trade_name.localeCompare(b.trade_name);
  });


  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentItems = filteredAndSorted.slice(firstIndex, lastIndex);
  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.periodicity-filter')) {
        setFilterPeriodicityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (error) return <ErrorPage />;

  return (
    <div className="flex flex-col h-full">
      <ActionLoader open={isSubmitting} onClose={() => {}} />

      <div className="flex-shrink-0">
        <Banner title="Lista de Socios" message="Visualiza y gestiona la información de todos los socios registrados." />
      </div>

      {/* Controles de búsqueda y filtros */}
      <div className="flex-shrink-0 px-4 py-4 bg-gray-50 border-b border-gray-200">
        {/* Barra de búsqueda */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre comercial, RUT o correo..."
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0488AF] focus:border-transparent text-black shadow-sm text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filtros y ordenamiento */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* Filtro de periodicidad */}
          <div className="relative periodicity-filter">
            <button 
              className={`flex items-center justify-between gap-2 px-3 py-1.5 border rounded-md text-xs font-medium transition-colors ${
                selectedPeriodicity 
                  ? "bg-[#0488AF] text-white border-[#0488AF]" 
                  : "bg-white text-gray-700 border-gray-300 hover:border-[#0488AF]"
              }`}
              onClick={() => setFilterPeriodicityOpen(!filterPeriodicityOpen)}
            >
              <Filter size={14} />
              <span>{selectedPeriodicity || "Periodicidad"}</span>
              <ChevronDown size={14} className={filterPeriodicityOpen ? "rotate-180" : ""} />
            </button>
            {filterPeriodicityOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                {["", "Mensual", "Semestral", "Anual"].map(period => (
                  <button
                    key={period}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-xs border-b border-gray-100 last:border-b-0 transition-colors"
                    onClick={() => {
                      setSelectedPeriodicity(period);
                      setFilterPeriodicityOpen(false);
                    }}
                  >
                    {period || "Todas"}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ordenamiento por fecha */}
          <button 
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-medium transition-colors ${
              sortBy === "entry_date" 
                ? "bg-[#0488AF] text-white border-[#0488AF]" 
                : "bg-white text-gray-700 border-gray-300 hover:border-[#0488AF]"
            }`}
            onClick={() => handleSort("entry_date")}
          >
            {sortBy === "entry_date" && sortDirection === "asc" ? 
              <SortAsc size={14} /> : 
              <SortDesc size={14} />
            }
            <span>Fecha</span>
          </button>

          {/* Ordenamiento por nombre */}
          <button 
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs font-medium transition-colors ${
              sortBy === "trade_name" 
                ? "bg-[#0488AF] text-white border-[#0488AF]" 
                : "bg-white text-gray-700 border-gray-300 hover:border-[#0488AF]"
            }`}
            onClick={() => handleSort("trade_name")}
          >
            {sortBy === "trade_name" && sortDirection === "asc" ?
              <SortAsc size={14} /> :
              <SortDesc size={14} />
            }
            <span>Nombre</span>
          </button>

          {/* Botón limpiar filtros */}
          {(selectedPeriodicity || sortBy !== "trade_name" || sortDirection !== "asc") && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 underline transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Contador de resultados */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-600">
            <span className="font-medium text-gray-900">{currentItems.length}</span> de{" "}
            <span className="font-medium text-gray-900">{filteredAndSorted.length}</span> socios
          </p>
        </div>
      </div>

      {/* Tabla con scroll interno */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          <div className="bg-white border-l border-r border-gray-200">
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RUT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comuna
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Periodicidad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.length > 0 ? (
                  currentItems.map(company => (
                    <tr
                      key={`${company.rut}-${company.contact_email}`}
                      className="hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => onSelectCompany(company)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap max-w-[180px] truncate" title={company.trade_name}>
                        <div className="text-sm font-medium text-gray-900">
                          {company.trade_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{company.rut}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap max-w-[160px] truncate" title={company.contact_email}>
                        <div className="text-sm text-gray-900">{company.contact_email}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap max-w-[100px] truncate" title={company.district}>
                        <div className="text-sm text-gray-900">{company.district}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          company.periodicity === "Mensual"
                            ? "bg-green-100 text-green-800"
                            : company.periodicity === "Semestral"
                            ? "bg-blue-100 text-blue-800"
                            : company.periodicity === "Anual"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}>
                          {company.periodicity}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{company.entry_date || "N/A"}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {company.rut !== user.rut && (
                          <div className="flex justify-center">
                            <button
                              aria-label={`Eliminar ${company.trade_name}`}
                              className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                              onClick={e => {
                                e.stopPropagation();
                                handleDelete(company);
                              }}
                              title={`Eliminar ${company.trade_name}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <div className="text-gray-500">
                        <div className="text-3xl mb-3">👥</div>
                        <div className="text-sm font-medium mb-1">No hay socios disponibles</div>
                        <div className="text-xs">
                          {searchTerm || selectedPeriodicity
                            ? "Intenta ajustar los filtros"
                            : "No se encontraron socios"}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>


      {/* Paginación fija */}
      {filteredAndSorted.length > itemsPerPage && (
        <div className="flex-shrink-0 bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-xs text-gray-700">
            Página <span className="font-medium">{currentPage}</span> de{" "}
            <span className="font-medium">{totalPages}</span>
          </div>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            siblingCount={0}
            boundaryCount={1}
            sx={{ 
              '& .MuiPaginationItem-root': {
                minWidth: '28px',
                height: '28px',
                fontSize: '0.75rem',
                '&.Mui-selected': {
                  backgroundColor: '#0488AF',
                  '&:hover': {
                    backgroundColor: '#037a9a',
                  }
                }
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

export default MemberList;